import {AfterViewInit, Component, Input} from '@angular/core';
import {InfluencerDetail} from '../../../models/influencer';
import {CommonConstant, listOrderedPlatforms} from '../../app.constants';
import {InfluencerService} from '../../services/influencer/influencer.service';
import {PdfExportService} from '../../services/export-pdf/pdf-export.service';
import {Router} from '@angular/router';
import {UserService} from '../../services/user.service';
import {Kpi} from '../../../models/common';
import {DeviceDetectorService} from 'ngx-device-detector';
import { WebUtils } from '../../utils/web.utils';

@Component({
  selector: 'app-influencer-basic-information',
  templateUrl: './influencer-basic-information.component.html',
  styleUrls: ['./influencer-basic-information.component.scss']
})
export class InfluencerBasicInformationComponent implements AfterViewInit {
  static COMPONENT_NAME = 'InfluencerBasicInformationComponent';
  @Input()
  public influencer: InfluencerDetail;
  @Input()
  public showCountryFlagIcon: boolean | true;
  @Input()
  public supportIgStoryLoader: boolean | false;
  @Input()
  public allowRedirectToInfluencerPage = false;
  @Input()
  public enableForMediaPlatform = true;

  // special case when we need to show a customized value of post and follower count
  @Input()
  public postCount: number;
  @Input()
  public followerCount: number;
  @Input()
  public customKpi: Kpi;

  public platformList = [];

  private _clickedTooltip;
  private _hoverTooltip;
  public tooltipChannels = [];

  constructor(
    private influencerService: InfluencerService,
    private pdfService: PdfExportService,
    private userService: UserService,
    private deviceService: DeviceDetectorService,
    private router: Router
  ) {
    this.platformList = listOrderedPlatforms.map(p => p.id);
  }

  get isInInfluencerApp() {
    return this.userService.isInfluencer;
  }

  hasPlatform(id: number) {
    if (!this.influencer) {
      return false;
    }
    let hasPlatform = false;
    if (Array.isArray(this.influencer.platforms)) {
      hasPlatform = this.influencer.platforms.filter(pl => pl.id === id).length > 0;
    }
    let hasChannel = false;
    if (Array.isArray(this.influencer.channels)) {
      hasChannel = this.influencer.channels.filter(channel => channel.platform.id === id).length > 0;
    }
    return hasPlatform || hasChannel;
  }

  getHandle(id: number) {
    if (!this.influencer || !this.influencer.channels) {
      return null;
    }
    const platformHandles = this.influencer.channels.filter(channel => channel.platform.id === id).map(c => c.handle);
    return platformHandles.join('\n');
  }

  get countryName() {
    if (!this.influencer || !this.influencer.country) {
      return null;
    }

    return this.influencer.country.name;
  }

  getPlatformIcon(id: number) {
    return this.influencerService.getPlatformIconById(id);
  }

  getCountryCode(influencer) {
    return influencer && influencer.country && influencer.country.code ?
      influencer.country.code.toLowerCase() : CommonConstant.INTERNATION_COUNTRY_CODE;
  }

  onLoadedAvatarUser() {
    this.pdfService.updateNumberImageLoaded(InfluencerBasicInformationComponent.COMPONENT_NAME);
  }

  openInfluencerDetails() {
    if (!this.allowRedirectToInfluencerPage) {
      return;
    }
    if (!this.enableForMediaPlatform) {
      return this.router.navigate(['dashboard', 'details']);
    }
    return this.router.navigate(['influencer', this.influencer.id, 'overview'], {
      queryParams: {
        activeAccount: this.userService.getCompanyId(),
      }
    });
  }

  get numberOfPost() {
    return this.postCount ? this.postCount : this.influencer ? this.influencer.numberOfPosts : null;
  }

  get numberOfFollower() {
    return this.followerCount ? this.followerCount : this.influencer ? this.influencer.numberOfFollowers : null;
  }

  get globalKpi(): Kpi {
    return this.customKpi ? this.customKpi : this.influencer ? this.influencer.globalKpi : null;
  }

  get onMobile() {
    return this.deviceService.isMobile();
  }

  ngAfterViewInit() {
    this.pdfService.imagedLoadedTrigger.subscribe(data => {
      if (data.component === InfluencerBasicInformationComponent.COMPONENT_NAME && data.currentCounter === 1) {
        this.pdfService.incrementNumberReportReady(InfluencerBasicInformationComponent.COMPONENT_NAME);
      }
    });
  }

  public toggleChannelIndicator(tooltip, platformId: number) {
    // Do nothing if no tooltip
    if (!tooltip) {
      return;
    }

    // close the current tooltip if click new tooltip
    if (this._clickedTooltip && this._clickedTooltip !== tooltip) {
      this._clickedTooltip.close();
    }

    // if user click on the open tooltip
    // keep it open if it is hovered
    // otherwise, close it
    if (tooltip.isOpen() && tooltip !== this._hoverTooltip) {
      tooltip.close();
      this._clickedTooltip = null;
      return;
    }

    if (this.openChannelIndicatorTooltip(tooltip, platformId)) {
      this._clickedTooltip = tooltip;
    }
  }

  private openChannelIndicatorTooltip(tooltip, platformId: number): boolean {
    if (!this.influencer || !this.influencer.channels) {
      return false;
    }

    this.tooltipChannels = this.influencer.channels
      .filter(channel => channel.platform.id === platformId)
      .map(c => {
        return {
          handle: c.handle,
          status: c.status,
          link: WebUtils.buildSocialChannelLink({
            name: c.platformName,
            domain: c.platform.domain
          }, c.handle)
        };
      });

    tooltip.open();
    return true;
  }

  public onClickOutsideTooltip(target: Element) {
    // Find the element to check where user click inside another tooltip
    // Inside => ignore because behavior will be handle in click handler
    // Not inside => close clicked popup
    const channelIndicatorContainerElement = target.closest('[data-type=channel-indicator]');
    if (channelIndicatorContainerElement) {
      return;
    }

    if (!this._clickedTooltip) {
      return;
    }

    this._clickedTooltip.close();
    this._clickedTooltip = null;
  }

  public onTooltipMouseEnter(tooltip, platformId: number) {
    if (!tooltip || this._clickedTooltip) {
      return;
    }

    if (this.openChannelIndicatorTooltip(tooltip, platformId)) {
      this._hoverTooltip = tooltip;
    }
  }

  public onTooltipMouseOut(tooltip) {
    if (!tooltip || this._clickedTooltip) {
      return;
    }

    if (tooltip.isOpen()) {
      this._hoverTooltip = null;
      tooltip.close();
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/main/influencer-basic-information/influencer-basic-information.component.ts